import React from 'react';
import PropTypes from 'prop-types';
import styled from 'src/ui/library/styled';
import cloneDeep from 'lodash.clonedeep';
import merge from 'lodash.merge';
import isEqual from 'lodash.isequal';
import Table from './components/Table';
import TableHead from './components/TableHead';
import TableBody from './components/TableBody';
import TablePagination from './components/TablePagination';
import TableProgress from './components/TableProgress';
import { TABLE_STATUS } from 'src/constants/constants';
import intl from 'react-intl-universal';
import TableScrollbar from './components/TableScrollbar';
import { saveCaseDataCollectQuestionnairePage } from 'src/services/questionnaire.service.js';
import { saveTaskDocument } from 'src/services/tasks.service.js';
import { DOCUMENTS_URL } from 'src/constants/constants';
import { fnGetDashboardView } from 'src/helpers/commonFunctions';

const styles = theme => ({
  root: {
    width: '100%',
    marginTop: '-1px',
  },
  table: {
    overflow: 'auto',
    height: '100%',
    position: 'relative',
    maxHeight: '499px',
    border: `solid 1px ${theme.colors.eyGrey3}`,
    paddingBottom: '2px', // in cases where there are 1 or 2 rows the scrollbar covers
  },
  noTableDataMessage: {
    margin: '5rem auto',
    textAlign: 'center',
    color: theme.colors.eyGrey1,
  },
  caption: {
    clip: 'rect(0 0 0 0)',
    height: '1px',
    margin: '-1px',
    overflow: 'hidden',
    padding: '0',
    position: 'absolute',
    width: '1px',
  },
  annouceText: {
    border: '0',
    clip: 'rect(0 0 0 0)',
    height: '1px',
    margin: '-1px',
    overflow: 'hidden',
    padding: '0',
    position: 'absolute',
    width: '1px',
  },
});

class TableBase extends React.Component {
  static propTypes = {
    /** Classname to be applied to <Table> */
    className: PropTypes.string,
    /** Styles to be applied to component */
    classes: PropTypes.object,
    /** Data that will be used to generate table columns */
    columns: PropTypes.PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        displayText: PropTypes.string.isRequired,
        visible: PropTypes.bool,
        sticky: PropTypes.bool,
        sortable: PropTypes.bool,
        renderComponent: PropTypes.function, // allows for custom cell rendering
        customSort: PropTypes.function,
      }),
    ).isRequired,
    /** Data that will be used to generate table rows */
    rows: PropTypes.array.isRequired,
    /** Table caption used for screen readers */
    caption: PropTypes.string,
    /**
     * Read Only mode will force table to ignore renderComponent per columns and display cell value.
     * It will also make rows not selectable.
     */
    readOnly: PropTypes.bool,
    /** Hide header */
    hideHeader: PropTypes.bool,
    /** Page for the table to open at */
    page: PropTypes.number,
    /** Enable/disable table pagination */
    pagination: PropTypes.bool,
    /** If provided, the total number of rows for the table */
    totalRows: PropTypes.number,
    /** If provided, the total number of rows per page */
    rowsPerPage: PropTypes.number,
    /** Server side mode means that the Table component will not handle pagination/sort. Useful for depedency on API's */
    serverSide: PropTypes.bool,
    /** If provided, the origRowIndex getting reset when sorting NOT handled on Server side i.e when server side mode is OFF*/
    resetDataRowOrigIndexOnClientSideSort: PropTypes.bool,
    /** Populate first column with selectable radio button */
    selectableRows: PropTypes.bool,
    /** Custom render that goes on LHS of pagination that displays when rows are selected */
    selectableRowsRender: PropTypes.bool,
    /** Use black scrollbar from <TableScrollBar /> */
    showBlackScrollbar: PropTypes.bool,
    /** Populate first column with 3dot action menu popper */
    actionable: PropTypes.bool,
    /** Override default action cell render */
    actionableRender: PropTypes.func,
    /** Default rows to have selected */
    defaultRowsSelected: PropTypes.array,
    /** Provide custom footer to display with the Table */
    footer: PropTypes.func,
    /** Callback function to set reference to internal setTableStatus() method */
    setTableStatus: PropTypes.func,
    /** Callback function to get reference to internal setTableModel() method */
    setTableModel: PropTypes.func,
    /** Callback function to get reference to internal updateColumnValue() method */
    updateColumn: PropTypes.func,
    /** Callback function to get reference to internal cancelSelectedRows() method */
    cancelSelectedRows: PropTypes.func,
    /** Callback function to get reference to internal updateModelValue() method */
    updateModel: PropTypes.func,
    /**
     * Callback function for table updates. fn(actionType: string, tableState: object)
     * tableState enum: ('rowClick', 'cellClick', 'pageChange', 'colSort', 'rowsSelected', 'cellValueUpdate')
     */
    onTableChange: PropTypes.func,
    /** Message for when no table data available */
    noTableDataMsg: PropTypes.string,
    tableHeadCellProps: PropTypes.object,
    showCheckbox: PropTypes.bool,
    notApplicable: PropTypes.bool,
    notSure: PropTypes.bool,
    parentanyCellSelected: PropTypes.func,
    rowTableData: PropTypes.object,
    isTaskDocument: PropTypes.bool,
    requestData: PropTypes.object,
    defaultFilterValues: PropTypes.object,
    setCheckboxChecked: PropTypes.func,
    isPageMandatory: PropTypes.bool,
    setCheckboxUpdated: PropTypes.func,
  };

  static defaultProps = {
    rowsPerPage: 25,
    serverSide: false,
    pagination: true,
    actionable: false,
    selectableRows: false,
    styleOverrides: {},
    defaultRowsSelected: [],
    readOnly: false,
    hideHeader: false,
    caption: '',
    tableHeadCellProps: {},
    showCheckbox: false,
    notApplicable: false,
    notSure: false,
    rowTableData: {},
    isTaskDocument: false,
    requestData: {},
    defaultFilterValues: {},
    isPageMandatory: false,
  };

  state = {
    order: 'asc',
    page: 0,
    orderBy: 'name',
    updateCount: 0,
    tableReady: TABLE_STATUS.initialize,
    rowCountHasChanged: false,
  };

  constructor(props) {
    super(props);
    this.tableModel = {
      columns: [],
      rows: [],
      selectedRows: props.defaultRowsSelected,
    };
    this.selectedRecordCount = 0;
  }

  componentDidMount() {
    this.setTableModel(this.props);

    if (this.props.setTableModel) {
      this.props.setTableModel(this.setTableModel.bind(this));
    }

    if (this.props.updateModel) {
      this.props.updateModel(this.updateModelValue.bind(this));
    }

    if (this.props.updateColumn) {
      this.props.updateColumn(this.updateColumnValue.bind(this));
    }

    if (this.props.cancelSelectedRows) {
      this.props.cancelSelectedRows(this.cancelSelectedRows.bind(this));
    }

    if (this.props.setTableStatus) {
      this.props.setTableStatus(this.setTableStatus.bind(this));
    }
    // if (this.props.parentanyCellSelected) {
    //   this.props.parentanyCellSelected(this.parentanyCellSelected.bind(this));
    // }
  }

  componentDidUpdate(prevProps) {
    if (this.props.resetPageToZero && prevProps.resetPageToZero !== this.props.resetPageToZero) {
      this.setState({ page: 0 });
    }

    if (prevProps.rows !== this.props.rows || prevProps.columns.length !== this.props.columns.length) {
      this.setTableModel(this.props, true);
    }
    if (prevProps.rows.length > this.props.rows.length) {
      this.setState({
        rowCountHasChanged: true,
      });
    }
    if (prevProps.rows.length < this.props.rows.length) {
      this.setState({
        rowCountHasChanged: false,
      });
    }
    if (this.props.notApplicable || this.props.notSure) {
      this.cancelSelectedRows(this.cancelSelectedRows.bind(this));
    }
  }

  handleTableChange = (actionType, additionalMeta = {}, showProgress = true) => {
    if (this.props.onTableChange !== undefined) {
      if (this.props.serverSide && showProgress) {
        if (this.tableRef) {
          this.tableRef.scrollTop = 0;
        }

        this.setState({ tableReady: TABLE_STATUS.loading }, () => {
          this.props.onTableChange(actionType, { model: this.tableModel, state: this.state, ...additionalMeta });
        });
      } else {
        this.props.onTableChange(actionType, { model: this.tableModel, state: this.state, ...additionalMeta });
      }
    }
  };

  setUpdateComponent = (rowIndex, colIndex, fn) => {
    this.tableModel.rows[rowIndex][colIndex].updateComponent = fn;
  };

  setUpdateSelectCell = (rowIndex, fn) => {
    this.tableModel.rows[rowIndex].forEach(col => {
      col.updateSelectCell = fn;
    });
  };

  setTableStatus = status => {
    this.setState({ tableReady: status });
  };

  updateColumnValue = (colIndex, property, value) => {
    // only re-render if there is truly a column value change - no-op
    if (this.tableModel.columns[colIndex][property] === value) {
      return;
    }

    const newColumns = cloneDeep(this.tableModel.columns);
    newColumns[colIndex] = { ...newColumns[colIndex], [property]: value };

    this.tableModel.columns = newColumns;
    this.setState({ tableReady: TABLE_STATUS.ready });
  };

  getIndexByOrigRowIndex = origRowIndex => {
    let finalRowIndex = null;

    for (let index = 0; index < this.tableModel.rows.length; index++) {
      const row = this.tableModel.rows[index];
      if (row[0].origRowIndex === origRowIndex) {
        finalRowIndex = index;
        break;
      }
    }

    return finalRowIndex;
  };

  updateModelValue = (rowIndex, colIndex, useOrigRowIndex = false, forceCellRender = false, value) => {
    let finalRowIndex = rowIndex;
    let origRowIndex = null;

    for (let index = 0; index < this.tableModel.rows.length; index++) {
      const row = this.tableModel.rows[index];
      if (row[0].origRowIndex === rowIndex) {
        origRowIndex = index;
        break;
      }
    }

    if (useOrigRowIndex) {
      finalRowIndex = origRowIndex;
    }

    this.tableModel.rows[finalRowIndex][colIndex].value = value;

    if (forceCellRender) {
      this.tableModel.rows[finalRowIndex][colIndex].updateComponent();
    }

    const cellMeta = { rowIndex, colIndex, origRowIndex: origRowIndex, value };
    this.handleTableChange('cellValueUpdate', { cellUpdated: cellMeta }, false);
  };

  rawColumns = cols => {
    return cols.map(item => {
      if (typeof item !== 'object') return item;

      const { options, renderComponent, ...otherOpts } = item;
      return otherOpts;
    });
  };

  carrySortDirection = (curColumn, previousModelColumns) => {
    const { id } = curColumn;

    if (id === undefined) {
      return curColumn.sortDirection || null;
    }

    const targetCol = previousModelColumns.find(col => col.id === id);
    let sortDirection = null;

    if (targetCol && targetCol.sortDirection !== undefined) {
      sortDirection = targetCol.sortDirection;
    }

    if (curColumn.sortDirection) {
      sortDirection = curColumn.sortDirection;
    }

    return sortDirection;
  };

  buildColumns = newColumns => {
    const modelColumns = this.rawColumns(this.tableModel.columns);
    const propColumns = this.rawColumns(newColumns);

    if (this.tableModel.rows && this.tableModel.rows.length && isEqual(modelColumns, propColumns)) {
      return this.tableModel.columns;
    }

    const columns = newColumns.map((column, colIndex) => {
      const columnOptions = {
        sortDirection: this.carrySortDirection(column, modelColumns),
        visible: true,
        ...column,
      };

      return columnOptions;
    });
    return columns;
  };

  setTableModel = (props, TABLE_UPDATE = false) => {
    const columns = this.buildColumns(props.columns);
    let newView = fnGetDashboardView();
    const { isTaskDocument, rowTableData, requestData, setCheckboxChecked, pageData } = this.props;
    //let selectedRows = props.selectedRows || this.tableModel.selectedRows;
    let selectedRows = [];
    let index = isTaskDocument ? 1 : 0;
    Object.entries(this.props.rows).forEach(([key, row]) => {
      if ((isTaskDocument && rowTableData && rowTableData[key].isChecked) || (row[1] && row[1].toLowerCase() === 'yes')) {
        const rowIdx = parseInt(key, 10);
        selectedRows.push({
          rowIndex: rowIdx,
          origRowIndex: rowIdx,
          isRowSelected: true,
          recordId: row[index],
          caseDocumentId: isTaskDocument ? requestData.rows[key].caseDocumentId : null,
        });
      }
    });
    if ((isTaskDocument || pageData) && newView) {
      // if(pageData)
      // {
      this.selectedRecordCount = requestData.selectedIds.length;
      setCheckboxChecked(selectedRows.length > 0 || this.props.notApplicable || this.props.notSure || requestData.anyRecordChecked);
      // }
      // else {
      //   setCheckboxChecked(selectedRows.length > 0);
      // }
    }

    const rows = props.rows.map((row, rowIndex) => {
      return columns.map((col, colIndex) => {
        return {
          columnId: col.id,
          origRowIndex: rowIndex, // when the table gets sorted it's good to have the orig row index value
          value: row[colIndex] || '',
          updateModelValue: this.updateModelValue,
          isRowSelected:
            row[1] === 'Yes' ||
            selectedRows.some(sr => {
              return sr.origRowIndex === rowIndex;
            }),
          updateSelectCell: () => {},
          isRenderComponent: col.renderComponent && props.readOnly === false ? true : false,
          renderComponent: col.renderComponent || undefined,
        };
      });
    });

    this.tableModel = { columns, rows, selectedRows };
    if (props.parentanyCellSelected) props.parentanyCellSelected(requestData.anyRecordChecked);
    this.setState(prevState => ({ tableReady: TABLE_STATUS.ready, updateCount: prevState.updateCount + 1 }));
  };

  getRowsPerPage = () => {
    const { rowsPerPage } = this.props;
    return rowsPerPage;
  };

  getPaginatedModel = () => {
    const { totalRows } = this.props;
    const { page } = this.state;
    const { rows, columns } = this.tableModel;
    const rowsPerPage = this.getRowsPerPage();
    const rowCount = totalRows || rows.length;
    let startIndex = page === 0 ? 0 : page * rowsPerPage;
    const endIndex = Math.min(rowCount, (page + 1) * rowsPerPage);

    // startIndex is equal to endIndex when user is on a page other than first
    // and that page has one row and user deletes that row, then Page no. has to change.
    if (startIndex === endIndex && page > 0) {
      this.setState({ page: page - 1 });
    }

    if (this.props.serverSide) {
      startIndex = 0;
    }

    /* build paginated rows now */
    let newRows = [];

    for (let rowIndex = startIndex; rowIndex < rowCount && rowIndex < endIndex; rowIndex++) {
      if (rows[rowIndex]) newRows.push(rows[rowIndex]);
    }

    return { rows: newRows, columns };
  };

  getVisibleRows = textToSearch => {
    let visibleRows = [];
    const { rows } = (this.props.pagination && this.getPaginatedModel()) || this.tableModel;

    if (!textToSearch) {
      return rows;
    }

    const needle = textToSearch.toString();

    rows.forEach((row, rowIndex) => {
      let isRowVisible = false;

      row.forEach((col, colIndex) => {
        if (col.toString().indexOf(needle) >= 0) {
          isRowVisible = true;
        }
      });

      if (isRowVisible) {
        visibleRows.push(row);
      }
    });

    return visibleRows;
  };

  sortComparsion(order) {
    return (a, b) => {
      if (a.value === null) a.value = '';
      if (b.value === null) b.value = '';
      return (
        (typeof a.value.localeCompare === 'function' ? a.value.localeCompare(b.value) : a.value - b.value) * (order === 'asc' ? -1 : 1)
      );
    };
  }

  cancelSelectedRows = () => {
    let newTableModel = { ...this.tableModel, selectedRows: [] };

    newTableModel.rows.forEach((row, rowIndex) => {
      row.forEach(col => {
        col.isRowSelected = false;
      });
      newTableModel.rows[rowIndex][0].updateSelectCell();
    });

    this.tableModel = newTableModel;
    this.handleTableChange('rowSelected', undefined, false);
  };

  onRowSelect = async selectedRow => {
    let newTableModel = { ...this.tableModel };
    let newView = fnGetDashboardView();
    const { origRowIndex } = selectedRow;
    const targetIndex = this.getIndexByOrigRowIndex(origRowIndex);
    let newSelectValue = false;
    const { isTaskDocument, requestData, defaultFilterValues, setCheckboxChecked, pageData, setCheckboxUpdated } = this.props;
    let recordIdIndex = isTaskDocument ? 1 : 0;

    const actionType = newTableModel.rows[targetIndex][0].isRowSelected ? 'remove' : 'add';
    let arrMultilineEntities = [];
    if (actionType === 'add') {
      newTableModel.selectedRows.push({
        ...selectedRow,
        isRowSelected: true,
        recordId: newTableModel.rows[targetIndex][recordIdIndex].value,
        caseDocumentId: isTaskDocument ? requestData.rows[targetIndex].caseDocumentId : null,
      });
      this.selectedRecordCount = this.selectedRecordCount + 1;
      newSelectValue = true;
      arrMultilineEntities.push({
        //...selectedRow,
        id: newTableModel.rows[targetIndex][recordIdIndex].value,
        isChecked: true,
        DocumentId: newTableModel.rows[targetIndex][recordIdIndex].value,
        IsChecked: true,
        CaseDocumentId: isTaskDocument ? requestData.rows[targetIndex].caseDocumentId : null,
      });
    } else {
      let newSelectedRows = newTableModel.selectedRows.filter(row => row.origRowIndex !== origRowIndex);
      newTableModel.selectedRows = newSelectedRows;
      newSelectValue = false;
      this.selectedRecordCount = this.selectedRecordCount - 1;
      arrMultilineEntities.push({
        //...selectedRow,
        id: newTableModel.rows[targetIndex][recordIdIndex].value,
        isChecked: false,
        DocumentId: newTableModel.rows[targetIndex][recordIdIndex].value,
        IsChecked: false,
        CaseDocumentId: isTaskDocument ? requestData.rows[targetIndex].caseDocumentId : null,
      });
    }
    if ((pageData || isTaskDocument) && newView) {
      /** bug fix 692170 - needs to be handled for questionnaire as well as documents* */
      // if(pageData)
      // {
      if (setCheckboxUpdated) {
        setCheckboxUpdated(true);
      }
      setCheckboxChecked(
        newTableModel.selectedRows.length > 0 || this.props.notApplicable || this.props.notSure || this.selectedRecordCount > 0,
      );
      // }
      // else {
      //   setCheckboxChecked(newTableModel.selectedRows.length > 0);
      // }
    }

    newTableModel.rows[targetIndex].forEach(col => (col.isRowSelected = newSelectValue));
    newTableModel.rows[targetIndex][0].updateSelectCell();
    // let arrMultilineEntities = [];
    // newTableModel.selectedRows.forEach(el =>
    //   arrMultilineEntities.push({
    //     id: el.recordId,
    //     isChecked: true,
    //     DocumentId: el.recordId,
    //     IsChecked: true,
    //     CaseDocumentId: el.caseDocumentId,
    //   }),
    // );
    // newTableModel.rows.forEach((el, ind) => {
    //   const index = arrMultilineEntities.findIndex(object => object.id === el[recordIdIndex].value);
    //   if (index === -1) {
    //     arrMultilineEntities.push({
    //       id: el[0].value,
    //       isChecked: false,
    //       DocumentId: el[recordIdIndex].value,
    //       IsChecked: false,
    //       CaseDocumentId: isTaskDocument ? requestData.rows[ind].caseDocumentId : null,
    //     });
    //   }
    // });

    this.tableModel = newTableModel;
    let selectedCount = newTableModel.rows.filter(function(value) {
      return value[0].isRowSelected === true;
    });
    if (this.props.parentanyCellSelected) this.props.parentanyCellSelected(selectedCount.length > 0 || this.selectedRecordCount > 0);

    if (!this.props.isTaskDocument) {
      const {
        organizerInstanceId,
        entityId,
        yearTypeId,
        organizerTypeId,
        pageId,
        sourceTableId,
        notApplicable,
        unSure,
      } = this.props.pageData;
      const pageState = true;
      const orgOptions = {
        entityId,
        organizerInstanceId,
        organizerTypeId,
        yearTypeId,
        pageId,
        pageSourceId: sourceTableId,
        languageId: 1,
        pageState,
        isSubmitted: false,
        dataRequests: [],
        tables: [],
        updateOnlyCheckBoxValue: true,
        notApplicable: selectedCount.length > 0 ? false : notApplicable,
        unSure: selectedCount.length > 0 ? false : unSure,
        multilineEntities: arrMultilineEntities,
      };

      try {
        await saveCaseDataCollectQuestionnairePage(orgOptions).catch(e => {
          this.props.toggleSpinner(false);
          throw e;
        });
      } catch (e) {
        this.props.toggleSpinner(false);
        throw e;
      }
    } else {
      const payload = {
        TaskId: defaultFilterValues.RequiredActionId,
        CaseId: defaultFilterValues.caseId,
        ProceedCompleteTask: false,
        NotApplicable: selectedCount.length > 0 ? false : this.props.notApplicable,
        UnSure: selectedCount.length > 0 ? false : this.props.notSure,
        Documents: arrMultilineEntities,
      };
      try {
        await saveTaskDocument(payload).catch(e => {
          this.props.toggleSpinner(false);
          throw e;
        });
      } catch (e) {
        this.props.toggleSpinner(false);
        throw e;
      }
    }
    this.handleTableChange('rowSelected', undefined, false);
  };

  onChangePage = page => {
    this.setState({ page }, () => {
      this.handleTableChange('changePage');
      if (!this.props.serverSide && this.props.showBlackScrollbar && this.props.scrollToTopOnPageChange) {
        this.tableScrollbarRef && this.tableScrollbarRef.scrollToTop();
      }
    });
  };

  onCellClick = cellMeta => {
    this.handleTableChange('cellClick', { cellClicked: cellMeta }, false);
  };

  onRowClick = (rowMeta, event) => {
    let curElement = event.target;
    while (curElement.tagName && !['TD', 'TR'].includes(curElement.tagName)) {
      curElement = curElement.parentNode;
    }

    // we do not want to register a 'rowClick' onChange for the action cell
    if (!curElement || curElement === window.document || (curElement && curElement.classList.contains('action-cell'))) {
      return;
    }

    this.handleTableChange('rowClick', { rowClicked: rowMeta }, false);
  };

  onViewDetail = rowMeta => {
    this.handleTableChange('viewDetail', { rowClicked: rowMeta }, false);
  };

  getMetaForMenu = additionalMeta => {
    return { model: this.tableModel, state: this.state, ...additionalMeta };
  };

  getAnnounceText = (col, direction) => {
    const directionText = direction === 'asc' ? 'ascending' : 'descending';
    return `Table now sorted by ${col} in ${directionText} order`;
  };

  onChangeSort = (column, colIndex) => {
    if (this.props.serverSide) {
      const newDirection = column.sortDirection === 'asc' ? 'desc' : 'asc';
      this.tableModel.columns[colIndex].sortDirection = newDirection;

      this.handleTableChange('changeSort', {
        sortedBy: { ...column, sortDirection: newDirection },
      });

      this.setState({ sortedBy: column.id, announceText: this.getAnnounceText(column.displayText, newDirection) });

      return;
    }

    /*
     * When given a column to sort first extract that column and sort it.
     * Once sorted take the row positions
     */
    const { rows, columns, ...otherModelProps } = this.tableModel;

    let dataToSort = rows.map((row, sortIndex) => ({
      value: row[colIndex].value,
      sortIndex,
    }));

    column.sortDirection = column.sortDirection === 'asc' ? 'desc' : 'asc';

    let sortOption = column.customSort ? column.customSort(column.sortDirection) : this.sortComparsion(column.sortDirection);

    dataToSort.sort(sortOption);

    let sortedRows = dataToSort.map(row => rows[row.sortIndex]);
    /**
     * The origRowIndex must need to reset when the sort operation
     * is Not handled on server side.
     * This is required because the origRowIndex evaluated initially
     * is in sync of table data array row indices and
     * once the data array sorted, the origRowIndex pointed to wrong row
     * which is causing issue in fetching row data in case of rowClicked event
     * The prop resetDataRowOrigIndexOnClientSideSort actually not required here
     * but it is added just to make sure this code run for client side sorting
     * which found in case of comp dashboard task list table only
     * In future, we can remove this prop check when
     * there are more number of uses Table component with
     * the prop serverSide set to false value or not provided
     */
    if (this.props.resetDataRowOrigIndexOnClientSideSort) {
      sortedRows = sortedRows.map((row, rowIndex) => {
        if (row && row.length && row[0]) {
          row[0].origRowIndex = rowIndex;
        }
        return row;
      });
    }
    this.tableModel = { columns, rows: sortedRows, ...otherModelProps };

    this.setState(
      { tableReady: true, sortedBy: column.id, announceText: this.getAnnounceText(column.displayText, column.sortDirection) },
      () => {
        this.handleTableChange('changeSort', { sortedBy: column });
      },
    );
  };

  getOverrideStyles = () => {
    const defaultStyles = {
      Table: {},
      TableHead: {},
      TableHeadCell: {},
      TableBody: {},
      TableBodyCell: {},
      TablePagination: {},
    };

    return merge(defaultStyles, this.props.styleOverrides);
  };

  setTableScrollbarRef = ref => {
    this.tableScrollbarRef = ref;
  };

  render() {
    const {
      classes,
      pagination,
      footer,
      selectableRows,
      selectableRowsRender,
      actionable,
      actionableRender,
      totalRows,
      readOnly,
      hideHeader,
      noTableDataMsg,
      caption,
      tableHeadCellProps,
      showBlackScrollbar,
      showCheckbox,
      notApplicable,
      notSure,
      rowTableData,
      onDelete,
      pageData,
      isTaskSubmitted,
    } = this.props;
    const { page, sortedBy, announceText } = this.state;
    const { columns } = this.tableModel;

    const visibleRows = this.getVisibleRows();
    const rowCount = totalRows || this.tableModel.rows.length;
    const rowsPerPage = pagination && this.getRowsPerPage();

    const styleOverrides = this.getOverrideStyles();

    let rootStyleOverride = {};
    let tableStyleOverride = {};

    if (styleOverrides.root) {
      rootStyleOverride = styleOverrides.root;
    }

    if (styleOverrides.table) {
      tableStyleOverride = styleOverrides.table;
    }

    return (
      <div className={classes.root} style={rootStyleOverride}>
        <div className={classes.table} ref={el => (this.tableRef = el)} style={tableStyleOverride}>
          <TableScrollbar isVisible={showBlackScrollbar} setTableScrollbarRef={this.setTableScrollbarRef}>
            <Table>
              <caption className={classes.caption}>{caption}</caption>
              <TableHead
                styleOverrides={{ TableHead: styleOverrides.TableHead, TableHeadCell: styleOverrides.TableHeadCell }}
                readOnly={readOnly}
                hideHeader={hideHeader}
                columns={columns}
                sortedBy={sortedBy}
                actionable={actionable}
                selectableRows={false}
                onChangeSort={this.onChangeSort}
                tableHeadCellProps={tableHeadCellProps}
              />
              <TableBody
                tableRef={this.tableRef}
                columns={columns}
                rows={visibleRows}
                readOnly={readOnly}
                actionable={actionable}
                actionableRender={actionableRender}
                selectableRows={selectableRows}
                onRowSelect={this.onRowSelect}
                onCellClick={this.onCellClick}
                onRowClick={this.onRowClick}
                onViewDetail={this.onViewDetail}
                getMetaForMenu={this.getMetaForMenu}
                setUpdateSelectCell={this.setUpdateSelectCell}
                setUpdateComponent={this.setUpdateComponent}
                styleOverrides={{ TableBody: styleOverrides.TableBody, TableBodyCell: styleOverrides.TableBodyCell }}
                noTableDataMsg={noTableDataMsg}
                showCheckbox={showCheckbox}
                notApplicable={notApplicable}
                notSure={notSure}
                rowTableData={rowTableData}
                onDelete={onDelete}
                pageData={pageData}
                isTaskSubmitted={isTaskSubmitted}
              />
            </Table>
            {this.state.tableReady === TABLE_STATUS.loading && <TableProgress />}
            {!visibleRows.length ? (
              <div className={classes.noTableDataMessage} aria-live="polite">
                {this.props.history && this.props.history.location.pathname.includes(DOCUMENTS_URL) && this.props.isCorpClient
                  ? `${intl.get('table.labels.noData')}`
                  : `${intl.get('table.labels.noResults') || noTableDataMsg}`}
              </div>
            ) : null}
          </TableScrollbar>
        </div>
        <div className={classes.annouceText} aria-live="polite">
          {announceText}
        </div>
        {footer && footer()}
        <div className={'table-pagination-container'}>
          {selectableRowsRender && selectableRowsRender()}
          {pagination && !!rowCount && (
            <TablePagination
              rowCount={rowCount}
              page={page}
              rowsPerPage={rowsPerPage}
              onChangePage={this.onChangePage}
              styleOverrides={{ TablePagination: styleOverrides.TablePagination }}
            />
          )}
        </div>
      </div>
    );
  }
}

export default styled(TableBase)(styles);
